import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import {
  Users, Target, Heart, Send, ArrowUpRight, ArrowDownLeft,
  CheckCircle, Clock, ChevronRight, UserPlus,
  Lightbulb, Star, MessageSquare, ArrowRight, CircleDot, HeartHandshake, AtSign, X, Sparkles, Plus, ChevronDown, ChevronUp, Bell, Search
} from "lucide-react";
import { toast } from "sonner";
import { trackToolOpened } from "../utils/analytics";
import { fbOnboardedUser } from "../utils/fbpixel";
import { SwotVideoNudge } from "../components/SwotVideo";
import InsightRequestDialog from "../components/InsightRequestDialog";
import ThankBackSheet from "../components/ThankBackSheet";
import GratitudeTimeline from "../components/GratitudeTimeline";
import { LinkedName, LinkedAvatar } from "../components/LinkedProfile";
// (MatchedPairs component intentionally not used on the dashboard per UX decision)

const API = "/api";

const getToolName = (t) => {
  if (t === "swot") return "Personal SWOT";
  if (t === "core_values") return "Core Values";
  if (t === "gratitude") return "Gratitude";
  return t;
};

// Relative-date formatter used by the dashboard feed & banners. Pulls the
// current UI language from i18next so "Today" / "Ayer" etc. respect the user's
// primary_language. Called from render, not top-level, so it always picks up
// the latest language after syncLanguageWithUser() runs.
const formatDate = (dateStr, t, lng) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  if (diff < 86400000) return t ? t("common.today") : "Today";
  if (diff < 172800000) return t ? t("common.yesterday") : "Yesterday";
  if (diff < 604800000) {
    const n = Math.floor(diff / 86400000);
    return t ? t("common.daysAgo", { count: n, defaultValue: `${n}d ago` }) : `${n}d ago`;
  }
  return d.toLocaleDateString(lng || "en-US", { month: "short", day: "numeric" });
};

// ── SVG Progress Ring ─────────────────────────────────────
const ProgressRing = ({ percent, size = 44, stroke = 3.5, color = "var(--color-brand-primary)" }) => {
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke="var(--color-border-default)" strokeWidth={stroke} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={stroke}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center"
        style={{ fontSize: size < 40 ? 10 : 11, fontWeight: 600, color: "var(--color-text-primary)" }}>
        {percent}%
      </span>
    </div>
  );
};

// ── Dynamic Subtitle ──────────────────────────────────────
const getDynamicSubtitle = (insightItems, contributors, acceptedNetwork, milestones) => {
  const pendingForMe = insightItems.filter(i => i.direction === "incoming" && i.status === "pending");
  if (pendingForMe.length > 0) {
    const name = pendingForMe[0].person_name?.split(" ")[0];
    return pendingForMe.length === 1
      ? `${name} is waiting for your perspective.`
      : `${pendingForMe.length} people are waiting for your perspective.`;
  }
  const received = insightItems.filter(i => i.direction === "outgoing" && i.status !== "pending");
  if (received.length > 0) {
    return `${received.length} insight${received.length > 1 ? "s" : ""} from your circle to review.`;
  }
  if (acceptedNetwork.length > 0) {
    return `Your circle has ${acceptedNetwork.length} people. Here's where things are growing.`;
  }
  return "Here's where things are growing.";
};

// ── Next Step Card (compact milestone) ────────────────────
const NextStepCard = ({ milestones, navigate, onRequestInsight }) => {
  if (!milestones) return null;
  const completedCount = milestones.filter(m => m.complete).length;
  const nextStep = milestones.find(m => !m.complete);
  if (!nextStep) return null;

  const pct = Math.round((nextStep.current / nextStep.target) * 100);
  const ctas = {
    network: { label: "Invite Someone", path: "/network?invite=true" },
    swot: { label: "Continue SWOT", path: "/tools/swot" },
    insights: { label: "Request Insight", action: onRequestInsight },
  };
  const cta = ctas[nextStep.id];

  return (
    <div className="mb-6" data-testid="next-step-card">
      <div className="rounded-xl border border-dashed overflow-hidden"
        style={{ borderColor: "var(--color-border-active)", background: "var(--color-brand-ghost)" }}>
        <div className="flex items-center gap-4 p-4">
          {/* Progress dots */}
          <div className="flex flex-col items-center gap-1 flex-shrink-0">
            {milestones.map((m, i) => (
              <div key={m.id} className={`rounded-full transition-all ${
                m.complete
                  ? "w-2.5 h-2.5 bg-green-500"
                  : m.id === nextStep.id
                    ? "w-3 h-3 border-2 border-current animate-pulse"
                    : "w-2 h-2 bg-slate-300"
              }`} style={m.id === nextStep.id ? { borderColor: "var(--color-brand-primary)" } : {}} />
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider mb-0.5"
              style={{ color: "var(--color-text-interactive)" }}>
              Next step &middot; {completedCount}/{milestones.length} done
            </p>
            <p className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
              {nextStep.label}
            </p>
            <p className="text-xs mt-0.5" style={{ color: "var(--color-text-secondary)" }}>
              {nextStep.description}
            </p>
            {/* Inline progress */}
            <div className="flex items-center gap-2 mt-2">
              <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-border-default)" }}>
                <div className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${pct}%`, background: "var(--color-brand-primary)" }} />
              </div>
              <span className="text-[11px] font-semibold" style={{ color: "var(--color-brand-primary)" }}>
                {nextStep.current}/{nextStep.target}
              </span>
            </div>
          </div>

          {/* CTA */}
          {cta && (
            <Button
              size="sm"
              className="h-8 text-xs rounded-full px-4 flex-shrink-0"
              style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
              onClick={() => cta.action ? cta.action() : navigate(cta.path)}
              data-testid={`next-step-cta-${nextStep.id}`}
            >
              {cta.label}
              <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Action Banner (pending insights for user) ─────────────
const ActionBanner = ({ pendingForMe, navigate }) => {
  const { t } = useTranslation();
  if (pendingForMe.length === 0) return null;
  const first = pendingForMe[0];

  return (
    <div className="mb-5" data-testid="action-banner">
      <div className="rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
        style={{
          background: "linear-gradient(135deg, var(--color-strengths-bg) 0%, var(--color-surface) 100%)",
          border: "1px solid var(--color-strengths-border)"
        }}
        onClick={() => navigate(`/insights/give/${first.request_id}`)}
      >
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "var(--color-success-bg)", border: "1px solid var(--color-success-border)" }}>
          <ArrowDownLeft className="w-4 h-4" style={{ color: "var(--color-success-text)" }} />
        </div>
        <LinkedAvatar username={first.person_username} className="flex-shrink-0">
          <Avatar className="h-8 w-8">
            <AvatarImage src={first.person_picture} alt={first.person_name} />
            <AvatarFallback className="text-xs" style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}>
              {first.person_name?.charAt(0) || "?"}
            </AvatarFallback>
          </Avatar>
        </LinkedAvatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
            <LinkedName username={first.person_username} name={first.person_name} /> {t("dashboard.waitingForPerspective", { name: "" }).replace(/^\s+/, "")}
          </p>
          <p className="text-xs flex items-center gap-1.5" style={{ color: "var(--color-text-secondary)" }}>
            {first.person_username && (
              <>
                <Link to={`/${first.person_username}`} onClick={(e) => e.stopPropagation()}
                  className="hover:text-violet-600" data-testid="action-banner-handle">
                  @{first.person_username}
                </Link>
                <span>&middot;</span>
              </>
            )}
            <span>{getToolName(first.tool_type)} &middot; {formatDate(first.created_at, t, undefined)}</span>
          </p>
        </div>
        <Button size="sm" className="h-8 text-xs rounded-full px-4 flex-shrink-0"
          style={{ background: "var(--color-success-text)", color: "#fff" }}
          data-testid="action-banner-give-btn"
          onClick={() => navigate(`/insight/${first.request_id}/respond`)}>
          {t("dashboard.giveInsight")}
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
      {pendingForMe.length > 1 && (
        <p className="text-xs text-center mt-1.5" style={{ color: "var(--color-text-interactive)" }}>
          +{pendingForMe.length - 1} {t("common.more")}
        </p>
      )}
    </div>
  );
};

// ── Username (@handle) Banner ─────────────────────────────
const UsernameBanner = ({ usernameInfo, onDismiss, navigate }) => {
  const { t } = useTranslation();
  if (!usernameInfo || !usernameInfo.username || usernameInfo.username_set_explicitly) return null;
  return (
    <div className="mb-5" data-testid="username-banner">
      <div className="rounded-xl p-4 flex items-center gap-3"
        style={{
          background: "linear-gradient(135deg, var(--color-brand-ghost) 0%, var(--color-surface) 100%)",
          border: "1px solid var(--color-border-active)"
        }}
      >
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "var(--color-surface)", border: "1px solid var(--color-border-active)" }}>
          <AtSign className="w-4 h-4" style={{ color: "var(--color-brand-primary)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
            {t("dashboard.usernameIs", "Your username is")} <span className="font-bold">@{usernameInfo.username}</span>
          </p>
          <p className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
            {t("dashboard.usernameHint", "This is how people will find and tag you. Keep it — or pick something else.")}
          </p>
        </div>
        <Button size="sm" className="h-8 text-xs rounded-full px-4 flex-shrink-0"
          style={{ background: "var(--color-brand-primary)", color: "#fff" }}
          onClick={() => navigate("/profile")}
          data-testid="username-banner-cta"
        >
          {t("dashboard.chooseUsername", "Choose username")}
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
        <button
          onClick={onDismiss}
          className="p-1.5 rounded-full hover:bg-white/50 flex-shrink-0 transition-colors"
          aria-label="Dismiss"
          data-testid="username-banner-dismiss"
        >
          <X className="w-4 h-4" style={{ color: "var(--color-text-secondary)" }} />
        </button>
      </div>
    </div>
  );
};

// ── Thank-Back Prompt Banner (reciprocity loop, 7-day window) ───
const ThankBackPromptBanner = ({ prompts, onOpen }) => {
  if (!prompts || prompts.length === 0) return null;
  const first = prompts[0];
  const firstName = (first.target_name || "them").split(" ")[0];

  return (
    <div className="mb-5" data-testid="thank-back-banner">
      <div className="rounded-xl p-4 flex items-center gap-3"
        style={{
          background: "linear-gradient(135deg, #faf5ff 0%, var(--color-surface) 100%)",
          border: "1px solid #e9d5ff",
        }}
      >
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "#faf5ff", border: "1px solid #e9d5ff" }}>
          <Heart className="w-4 h-4 fill-violet-500" style={{ color: "#8b5cf6" }} />
        </div>
        <LinkedAvatar username={first.target_username} className="flex-shrink-0">
          <Avatar className="h-8 w-8">
            <AvatarImage src={first.target_picture} alt={first.target_name} />
            <AvatarFallback className="text-xs" style={{ background: "#f5f3ff", color: "#7c3aed" }}>
              {firstName.charAt(0)}
            </AvatarFallback>
          </Avatar>
        </LinkedAvatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
            <LinkedName username={first.target_username} name={`${firstName} added your gratitude to their wall`}>
              {firstName}
            </LinkedName>
            {" "}added your gratitude to their wall
          </p>
          <p className="text-xs truncate flex items-center gap-1.5" style={{ color: "var(--color-text-secondary)" }}>
            {first.target_username && (
              <>
                <Link to={`/${first.target_username}`} onClick={(e) => e.stopPropagation()}
                  className="hover:text-violet-600" data-testid="thank-back-banner-handle">
                  @{first.target_username}
                </Link>
                <span>&middot;</span>
              </>
            )}
            <span>Want to say more?</span>
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => onOpen(first)}
          className="h-8 text-xs rounded-full px-4 flex-shrink-0"
          style={{ background: "#8b5cf6", color: "#fff" }}
          data-testid="thank-back-cta"
        >
          <Heart className="w-3 h-3 mr-1 fill-white" />
          Thank back
        </Button>
      </div>
      {prompts.length > 1 && (
        <p className="text-xs text-center mt-1.5" style={{ color: "var(--color-text-interactive)" }}>
          +{prompts.length - 1} more to thank back
        </p>
      )}
    </div>
  );
};

// ── Gratitude Connection Request Banner (from public wall) ─
const GratitudeRequestBanner = ({ requests, onRefresh }) => {
  if (!requests || requests.length === 0) return null;
  const first = requests[0];

  const handleAction = async (action) => {
    try {
      await fetch(`${API}/gratitude/pending-requests/${first.card_id}/${action}`, {
        method: "POST",
        credentials: "include",
      });
      if (action === "accept") {
        const suffix = first.sender_username ? ` (@${first.sender_username})` : "";
        toast.success(`${first.sender_name.split(" ")[0]}${suffix} joined your Circle`);
      }
      else toast.success("Ignored");
      onRefresh?.();
    } catch {
      toast.error("Couldn't update");
    }
  };

  return (
    <div className="mb-5" data-testid="gratitude-request-banner">
      <div className="rounded-xl p-4 flex items-center gap-3"
        style={{
          background: "linear-gradient(135deg, #fdf4ff 0%, var(--color-surface) 100%)",
          border: "1px solid #f3d1ff"
        }}
      >
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "#fae8ff", border: "1px solid #f3d1ff" }}>
          <HeartHandshake className="w-4 h-4" style={{ color: "#a21caf" }} />
        </div>
        <LinkedAvatar username={first.sender_username} className="flex-shrink-0">
          <Avatar className="h-8 w-8">
            <AvatarImage src={first.sender_picture} alt={first.sender_name} />
            <AvatarFallback className="text-xs" style={{ background: "#fae8ff", color: "#a21caf" }}>
              {first.sender_name?.charAt(0) || "?"}
            </AvatarFallback>
          </Avatar>
        </LinkedAvatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
            <LinkedName username={first.sender_username} name={first.sender_name}>
              {first.sender_name}
            </LinkedName>
            {" "}sent you gratitude
          </p>
          <p className="text-xs truncate flex items-center gap-1.5" style={{ color: "var(--color-text-secondary)" }}>
            {first.sender_username && (
              <>
                <Link to={`/${first.sender_username}`} onClick={(e) => e.stopPropagation()}
                  className="hover:text-violet-600" data-testid="gratitude-request-banner-handle">
                  @{first.sender_username}
                </Link>
                <span>&middot;</span>
              </>
            )}
            <span>As your {first.relationship} · from your public wall</span>
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => handleAction("accept")}
          className="h-8 text-xs rounded-full px-3 flex-shrink-0"
          style={{ background: "#a21caf", color: "#fff" }}
          data-testid="gratitude-request-accept-btn"
        >
          Accept
        </Button>
        <Button
          size="sm"
          onClick={() => handleAction("ignore")}
          variant="ghost"
          className="h-8 text-xs rounded-full px-3 flex-shrink-0 text-slate-500"
          data-testid="gratitude-request-ignore-btn"
        >
          Ignore
        </Button>
      </div>
      {requests.length > 1 && (
        <p className="text-xs text-center mt-1.5" style={{ color: "var(--color-text-interactive)" }}>
          +{requests.length - 1} more waiting
        </p>
      )}
    </div>
  );
};

// ── Gratitude Ask Banner (pending gratitude asks) ─────────
const GratitudeAskBanner = ({ pendingAsks, navigate }) => {
  if (!pendingAsks || pendingAsks.length === 0) return null;
  const first = pendingAsks[0];

  return (
    <div className="mb-5" data-testid="gratitude-ask-banner">
      <div className="rounded-xl p-4 flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
        style={{
          background: "linear-gradient(135deg, #fff7ed 0%, var(--color-surface) 100%)",
          border: "1px solid #fed7aa"
        }}
        onClick={() => navigate(`/tools/gratitude?respond=${first.request_id}`)}
      >
        <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}>
          <HeartHandshake className="w-4 h-4" style={{ color: "#c2410c" }} />
        </div>
        <LinkedAvatar username={first.requester_username} className="flex-shrink-0">
          <Avatar className="h-8 w-8">
            <AvatarImage src={first.requester_picture} alt={first.requester_name} />
            <AvatarFallback className="text-xs" style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}>
              {first.requester_name?.charAt(0) || "?"}
            </AvatarFallback>
          </Avatar>
        </LinkedAvatar>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
            <LinkedName username={first.requester_username} name={first.requester_name}>
              {first.requester_name}
            </LinkedName>
            {" "}is asking for your gratitude
          </p>
          <p className="text-xs truncate flex items-center gap-1.5" style={{ color: "var(--color-text-secondary)" }}>
            {first.requester_username && (
              <>
                <Link to={`/${first.requester_username}`} onClick={(e) => e.stopPropagation()}
                  className="hover:text-violet-600" data-testid="gratitude-ask-banner-handle">
                  @{first.requester_username}
                </Link>
                <span>&middot;</span>
              </>
            )}
            <span className="truncate">{first.prompt || "What are you grateful for in them?"}</span>
          </p>
        </div>
        <Button size="sm" className="h-8 text-xs rounded-full px-4 flex-shrink-0"
          style={{ background: "#c2410c", color: "#fff" }}
          data-testid="gratitude-ask-banner-respond-btn"
        >
          Respond
          <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </div>
      {pendingAsks.length > 1 && (
        <p className="text-xs text-center mt-1.5" style={{ color: "var(--color-text-interactive)" }}>
          +{pendingAsks.length - 1} more asking for your gratitude
        </p>
      )}
    </div>
  );
};

// ── Insight Item ──────────────────────────────────────────
const InsightItem = ({ item, navigate }) => {
  const { t, i18n } = useTranslation();
  const isOutgoing = item.direction === "outgoing";
  const isPending = item.status === "pending";
  const isNotif = item.status === "notification";
  const isReceived = isOutgoing && !isPending && !isNotif;
  const isGiven = !isOutgoing && !isPending && !isNotif;
  const isGratitude = item.tool_type === "gratitude";

  // Render the label as (prefix) + <LinkedName>{name}</LinkedName> so the
  // person's name portion is always a clickable link to /{username}. Prefix
  // stays plain text to keep the row visually calm. EVERY status gets a
  // descriptive verb so the row reads as a complete action, not just a name.
  let labelPrefix = "";
  let labelName = item.person_name || "";
  let labelIsSimple = false; // If true, we render the simple `label` string.
  let label = null;
  if (isNotif) { label = item.title || item.message || `Update from ${item.person_name}`; labelIsSimple = true; }
  else if (isGratitude) { labelPrefix = t("dashboard.gratitudeFromPrefix", { defaultValue: "Gratitude from" }); }
  else if (isOutgoing && isPending) { labelPrefix = t("dashboard.awaitingInsightFromPrefix", { defaultValue: "Awaiting insight from" }); }
  else if (isReceived) { labelPrefix = t("dashboard.insightFromPrefix", { defaultValue: "New insight from" }); }
  else if (isGiven) { labelPrefix = t("dashboard.yourInsightForPrefix", { defaultValue: "Your insight for" }); }
  else if (!isOutgoing && isPending) { labelPrefix = t("dashboard.needsInsightFromPrefix", { defaultValue: "Needs your insight —" }); }

  let iconBg, iconEl, accentBorder;
  if (isNotif) {
    iconBg = "var(--color-brand-ghost)";
    iconEl = <Bell className="w-3.5 h-3.5" style={{ color: "var(--color-brand-primary)" }} />;
    accentBorder = item.read ? null : "var(--color-brand-primary)";
  } else if (isReceived) {
    iconBg = "var(--color-strengths-bg)";
    iconEl = <ArrowDownLeft className="w-3.5 h-3.5" style={{ color: "var(--color-strengths-text)" }} />;
    accentBorder = "var(--color-strengths-border)";
  } else if (isOutgoing && isPending) {
    iconBg = "var(--color-opportunities-bg)";
    iconEl = <ArrowUpRight className="w-3.5 h-3.5" style={{ color: "var(--color-opportunities-text)" }} />;
    accentBorder = null;
  } else if (!isOutgoing && isPending) {
    iconBg = "var(--color-threats-bg)";
    iconEl = <ArrowDownLeft className="w-3.5 h-3.5" style={{ color: "var(--color-threats-text)" }} />;
    accentBorder = "var(--color-threats-border)";
  } else {
    iconBg = "var(--color-surface-subtle)";
    iconEl = <ArrowUpRight className="w-3.5 h-3.5" style={{ color: "var(--color-text-placeholder)" }} />;
    accentBorder = null;
  }

  const isClickable = (!isOutgoing && isPending) || isReceived || isGiven || isNotif;

  return (
    <div
      className={`flex items-start gap-2.5 sm:gap-3 p-3 rounded-lg transition-colors ${isClickable ? "cursor-pointer" : ""}`}
      style={{
        borderLeft: accentBorder ? `3px solid ${accentBorder}` : "3px solid transparent",
        background: (!isOutgoing && isPending) ? "var(--color-brand-ghost)" : "transparent",
      }}
      onClick={() => {
        if (isNotif) {
          // Route by notification type to the most useful destination.
          const t = item.notif_type || "";
          if (t === "insight_response" && item.request_id) navigate(`/insights/view/${item.request_id}`);
          else if (t === "insight_request" && item.request_id) navigate(`/insights/give/${item.request_id}`);
          else if (t === "insight_message" && item.request_id) navigate(`/insights/view/${item.request_id}`);
          else if (t === "suggestion_accepted") navigate("/tools/swot");
          else if (t.startsWith("gratitude")) navigate("/tools/gratitude");
          else navigate("/notifications");
          return;
        }
        if (isGratitude) navigate("/tools/gratitude?tab=received");
        else if (!isOutgoing && isPending) navigate(`/insights/give/${item.request_id}`);
        else if (isReceived) navigate(`/insights/view/${item.request_id}`);
        else if (isGiven) navigate(`/insights/give/${item.request_id}`);
      }}
      data-testid={`insight-item-${item.request_id}`}
    >
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
        style={{ background: iconBg }}>
        {iconEl}
      </div>
      <LinkedAvatar username={item.person_username} className="flex-shrink-0 mt-0.5 flex">
        <Avatar className="h-8 w-8">
          <AvatarImage src={item.person_picture} alt={item.person_name} />
          <AvatarFallback className="text-xs" style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}>
            {item.person_name?.charAt(0) || "?"}
          </AvatarFallback>
        </Avatar>
      </LinkedAvatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium leading-snug" style={{ color: "var(--color-text-primary)" }}>
            {labelIsSimple ? label : (
              <>
                {labelPrefix && <span>{labelPrefix} </span>}
                <LinkedName
                  username={item.person_username}
                  name={labelName}
                  testId={`insight-item-name-${item.request_id}`}
                />
              </>
            )}
          </p>
          <div className="flex-shrink-0">
            {isNotif ? (
              !item.read ? (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
                  style={{ color: "var(--color-brand-primary)", background: "var(--color-brand-ghost)" }}>
                  <Bell className="w-2.5 h-2.5" /> New
                </span>
              ) : null
            ) : isPending ? (
              isOutgoing ? (
                <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
                  style={{ color: "var(--color-opportunities-text)", background: "var(--color-opportunities-bg)" }}>
                  <Clock className="w-2.5 h-2.5" /> {t("dashboard.statusAwaiting")}
                </span>
              ) : (
                <Button size="sm"
                  className="h-7 text-xs rounded-full px-3 whitespace-nowrap"
                  style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
                  onClick={(e) => { e.stopPropagation(); navigate(`/insights/give/${item.request_id}`); }}
                  data-testid={`give-insight-btn-${item.request_id}`}
                >
                  {t("dashboard.giveInsight")}
                </Button>
              )
            ) : isReceived ? (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex items-center gap-1 whitespace-nowrap"
                style={{ color: "var(--color-success-text)", background: "var(--color-success-bg)" }}>
                <CheckCircle className="w-2.5 h-2.5" /> {t("dashboard.statusReceived")}
              </span>
            ) : isGiven ? (
              <Button size="sm"
                className="h-7 text-xs rounded-full px-3 whitespace-nowrap"
                variant="outline"
                style={{ borderColor: "var(--color-brand-primary)", color: "var(--color-brand-primary)" }}
                onClick={(e) => { e.stopPropagation(); navigate(`/insights/give/${item.request_id}`); }}
                data-testid={`update-insight-btn-${item.request_id}`}
              >
                Update
              </Button>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs mt-0.5" style={{ color: "var(--color-text-placeholder)" }}>
          <span>{getToolName(item.tool_type)}</span>
          {item.person_username && (
            <>
              <span>&middot;</span>
              <Link to={`/${item.person_username}`} onClick={(e) => e.stopPropagation()}
                className="hover:text-violet-600"
                data-testid={`insight-item-handle-${item.request_id}`}
              >
                @{item.person_username}
              </Link>
            </>
          )}
          {item.role && (
            <>
              <span>&middot;</span>
              <span className="capitalize">{item.role}</span>
            </>
          )}
          {item.created_at && (
            <>
              <span>&middot;</span>
              <span>{formatDate(item.created_at, t, i18n.language)}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Gratitude Pulse (summary of the Gratitude tool) ──────
// Composition: (A) Headline trio — Given / Received / Self counts this month
// with ▲▼ delta vs. last month, and (D) Life-area distribution — horizontal
// stacked bar (this month) using wheelAreaMap colors. Replaces the big
// timeline in the main column; the timeline itself is moved to the right
// sidebar (compact variant="dashboard").
const COLOR_GIVEN = "#E3638E";   // rose — matches GratitudeTimeline
const COLOR_RECEIVED = "#7C5FD4"; // violet — matches GratitudeTimeline
const COLOR_SELF = "#94A3B8";     // neutral fallback for "self"

const PulseStat = ({ label, value, prev, color, showDelta, testId }) => {
  const delta = value - prev;
  const arrow = delta > 0 ? "▲" : delta < 0 ? "▼" : "·";
  const deltaColor =
    delta > 0 ? "var(--color-strengths-text)"
    : delta < 0 ? "var(--color-weaknesses-text)"
    : "var(--color-text-placeholder)";
  return (
    <div className="flex-1 min-w-0" data-testid={testId}>
      <div className="flex items-baseline gap-1">
        <span className="text-lg font-bold leading-none" style={{ color }}>{value}</span>
        {showDelta && (
          <span className="text-[10px] font-semibold" style={{ color: deltaColor }} data-testid={`${testId}-delta`}>
            {arrow}{Math.abs(delta)}
          </span>
        )}
      </div>
      <p className="text-[9px] uppercase tracking-wider mt-0.5" style={{ color: "var(--color-text-placeholder)" }}>
        {label}
      </p>
    </div>
  );
};

const GratitudePulse = ({ cards, wheelAreaMap }) => {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);

  const validCards = (cards || []).filter(c =>
    c?.created_at && !c.deleted &&
    !(c.perspective === "received" && c.target_visibility === "removed")
  );

  const thisMonth = { given: 0, received: 0, self: 0 };
  const lastMonth = { given: 0, received: 0, self: 0 };
  const areaCounts = {};

  validCards.forEach(c => {
    const d = new Date(c.created_at);
    if (Number.isNaN(d.getTime())) return;
    let bucket = null;
    if (d >= thisMonthStart) bucket = thisMonth;
    else if (d >= lastMonthStart) bucket = lastMonth;
    if (bucket) {
      if (c.perspective === "given") bucket.given++;
      else if (c.perspective === "received") bucket.received++;
      else if (c.perspective === "mine") bucket.self++;
    }
    // Area distribution — this month only. Multi-area self-cards contribute
    // to each linked area independently (matching the timeline's dot logic).
    if (d >= thisMonthStart) {
      const keys = Array.isArray(c.wheel_area_keys) && c.wheel_area_keys.length
        ? c.wheel_area_keys
        : (c.wheel_area_key ? [c.wheel_area_key] : ["_other"]);
      for (const k of keys) {
        areaCounts[k] = (areaCounts[k] || 0) + 1;
      }
    }
  });

  const thisTotal = thisMonth.given + thisMonth.received + thisMonth.self;
  const lastTotal = lastMonth.given + lastMonth.received + lastMonth.self;
  const totalArea = Object.values(areaCounts).reduce((a, b) => a + b, 0);
  const areaSegments = Object.entries(areaCounts)
    .map(([key, count]) => ({
      key,
      count,
      pct: totalArea > 0 ? (count / totalArea) * 100 : 0,
      label: wheelAreaMap[key]?.label || (key === "_other" ? t("common.other") : key),
      color: wheelAreaMap[key]?.color || "#cbd5e1",
    }))
    .sort((a, b) => b.count - a.count);

  const monthName = now.toLocaleDateString("en-US", { month: "long" });

  // Empty state — keep the card present so the Gratitude tool is always anchored
  if (validCards.length === 0) {
    return (
      <div
        className="rounded-xl p-4"
        style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}
        data-testid="gratitude-pulse-empty"
      >
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
            <HeartHandshake className="w-4 h-4" style={{ color: COLOR_GIVEN }} />
            Gratitude pulse
          </h3>
        </div>
        <p className="text-[11px] mb-3" style={{ color: "var(--color-text-secondary)" }}>
          Write your first gratitude to start your pulse — a monthly read of what you've given, received, and noticed.
        </p>
        <Link
          to="/tools/gratitude"
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors hover:opacity-90"
          style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}
          data-testid="gratitude-pulse-start"
        >
          <Plus className="w-3 h-3" /> Start your wall
        </Link>
      </div>
    );
  }

  const showDelta = lastTotal > 0;

  return (
    <div
      className="rounded-xl p-4"
      style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}
      data-testid="gratitude-pulse"
    >
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="text-sm font-semibold flex items-center gap-2 min-w-0" style={{ color: "var(--color-text-primary)" }}>
          <HeartHandshake className="w-4 h-4 flex-shrink-0" style={{ color: COLOR_GIVEN }} />
          <span className="truncate">{t("dashboard.gratitudePulse")}</span>
        </h3>
        <span className="text-[10px] flex-shrink-0" style={{ color: "var(--color-text-placeholder)" }}>
          {monthName}
        </span>
      </div>

      {/* (A) Headline trio — compact, no dividers */}
      <div className="flex items-start gap-2 pb-3 mb-3" style={{ borderBottom: "0.5px solid var(--color-border-default)" }}>
        <PulseStat label={t("dashboard.given")}    value={thisMonth.given}    prev={lastMonth.given}    color={COLOR_GIVEN}    showDelta={showDelta} testId="pulse-given" />
        <PulseStat label={t("dashboard.received")} value={thisMonth.received} prev={lastMonth.received} color={COLOR_RECEIVED} showDelta={showDelta} testId="pulse-received" />
        <PulseStat label={t("dashboard.self")}     value={thisMonth.self}     prev={lastMonth.self}     color={COLOR_SELF}     showDelta={showDelta} testId="pulse-self" />
      </div>

      {/* (D) Life-area distribution */}
      <div>
        <p className="text-[9px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--color-text-secondary)" }}>
          {t("dashboard.whereItFlows")}
        </p>
        {totalArea > 0 ? (
          <>
            <div
              className="flex h-2 rounded-full overflow-hidden"
              style={{ background: "var(--color-surface-subtle)" }}
              data-testid="pulse-area-bar"
            >
              {areaSegments.map(s => (
                <div
                  key={s.key}
                  style={{ width: `${s.pct}%`, background: s.color }}
                  title={`${s.label}: ${s.count} (${Math.round(s.pct)}%)`}
                  data-testid={`pulse-area-seg-${s.key}`}
                />
              ))}
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-1 mt-2">
              {areaSegments.slice(0, 3).map(s => (
                <div key={s.key} className="flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
                  <span className="text-[10px] truncate max-w-[90px]" style={{ color: "var(--color-text-secondary)" }}>{s.label}</span>
                  <span className="text-[10px] font-semibold" style={{ color: "var(--color-text-primary)" }}>
                    {Math.round(s.pct)}%
                  </span>
                </div>
              ))}
              {areaSegments.length > 3 && (
                <span className="text-[10px]" style={{ color: "var(--color-text-placeholder)" }}>
                  +{areaSegments.length - 3}
                </span>
              )}
            </div>
          </>
        ) : (
          <p className="text-[11px]" style={{ color: "var(--color-text-placeholder)" }}>
            Tag life areas on your gratitudes to see how your focus is distributed.
          </p>
        )}
      </div>
    </div>
  );
};

// ── Circle Preview (horizontal avatar row) ────────────────
const CirclePreview = ({ connections }) => {
  const { t } = useTranslation();
  const accepted = connections.filter(c => c.status === "accepted");
  const [search, setSearch] = useState("");
  const filtered = search.trim()
    ? accepted.filter(c => {
        const name = (c.display_name || c.name || "").toLowerCase();
        const handle = (c.display_username || "").toLowerCase();
        const q = search.trim().toLowerCase();
        return name.includes(q) || handle.includes(q);
      })
    : accepted;
  return (
    <div className="rounded-xl p-4" style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }} data-testid="circle-preview">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
          <Users className="w-4 h-4" style={{ color: "var(--color-brand-primary)" }} />
          {t("dashboard.yourCircle")}
        </h3>
        <Link to="/network" className="text-xs font-medium" style={{ color: "var(--color-text-interactive)" }} data-testid="view-circle-link">
          {t("common.viewAll")}
        </Link>
      </div>
      {accepted.length > 0 ? (
        <>
          {/* Search input — only renders when there's >5 connections so small
              circles aren't bothered. Filters the avatar strip below. */}
          {accepted.length > 5 && (
            <div className="relative mb-3">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none"
                style={{ color: "var(--color-text-placeholder)" }}
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("dashboard.searchCirclePlaceholder")}
                data-testid="circle-search-input"
                className="w-full text-xs rounded-full pl-8 pr-3 py-1.5 outline-none focus:ring-2 focus:ring-violet-400"
                style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.10)",
                  color: "var(--color-text-primary)",
                }}
              />
            </div>
          )}

          {/* Horizontally scrollable avatar strip — every accepted connection
              gets a profile picture; user pans sideways to browse. The native
              scrollbar is hidden; instead we apply a subtle fade-edge mask so
              the user sees there's more content past the visible area. */}
          <div
            className="relative -mx-1 mb-3"
            data-testid="circle-strip-wrap"
            style={{
              maskImage: "linear-gradient(to right, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%)",
              WebkitMaskImage: "linear-gradient(to right, transparent 0, #000 16px, #000 calc(100% - 16px), transparent 100%)",
            }}
          >
            <div
              className="flex items-center gap-2 overflow-x-auto px-1 py-0.5"
              data-testid="circle-avatar-strip"
              style={{
                scrollSnapType: "x proximity",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {/* WebKit scrollbar hide via CSS-in-JS isn't trivial; rely on
                  Tailwind's `[&::-webkit-scrollbar]:hidden` arbitrary variant. */}
              <style>{`[data-testid="circle-avatar-strip"]::-webkit-scrollbar{display:none}`}</style>
              {filtered.length > 0 ? filtered.map((conn, i) => (
                <LinkedAvatar
                  key={conn.connection_id || i}
                  username={conn.display_username}
                  className="block flex-shrink-0"
                  stopPropagation={false}
                  style={{ scrollSnapAlign: "start" }}
                >
                  <Avatar
                    className="h-10 w-10 ring-2 transition-transform hover:scale-110 cursor-pointer"
                    style={{ ["--tw-ring-color"]: "rgba(167,139,250,0.30)" }}
                    title={conn.display_name || conn.name}
                    data-testid={`circle-avatar-${conn.connection_id}`}
                  >
                    {conn.display_picture && <AvatarImage src={conn.display_picture} alt={conn.display_name || conn.name} />}
                    <AvatarFallback
                      className="text-xs font-semibold"
                      style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}
                    >
                      {(conn.display_name || conn.name)?.charAt(0) || "?"}
                    </AvatarFallback>
                  </Avatar>
                </LinkedAvatar>
              )) : (
                <span className="text-xs flex-1 px-2 py-2" style={{ color: "var(--color-text-placeholder)" }}>
                  {t("dashboard.noMatches")}
                </span>
              )}
            </div>
          </div>

          {/* Connections count + Invite CTA stacked below the strip. */}
          <div className="flex items-center justify-between gap-3">
            <span className="text-[11px]" style={{ color: "var(--color-text-secondary)" }}>
              {t("dashboard.connections", { count: accepted.length })}
            </span>
            <Link
              to="/network?invite=true"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all hover:scale-[1.02]"
              style={{
                background: "var(--color-brand-primary)",
                color: "var(--color-text-on-brand)",
                boxShadow: "0 2px 8px rgba(124, 58, 237, 0.30)",
              }}
              data-testid="circle-invite-btn"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {t("dashboard.inviteToCircle")}
            </Link>
          </div>
        </>
      ) : (
        <div className="text-center py-3">
          <p className="text-xs mb-2" style={{ color: "var(--color-text-secondary)" }}>Start building your support circle</p>
          <Link to="/network?invite=true">
            <Button size="sm" className="h-7 text-xs rounded-full"
              style={{ background: "var(--color-brand-primary)", color: "var(--color-text-on-brand)" }}>
              <UserPlus className="w-3 h-3 mr-1" /> Invite Someone
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
};

// ── People You Might Know (mutual-connection suggestions) ─
// Renders only when the backend returns >=1 candidate. Hidden state by
// default shows max 5; an inline "Show more" expands to the full list.
// Per-row Invite goes to the user's public profile (where the existing
// connect dialog with role-picker lives) and Dismiss soft-hides them.
const PeopleYouMightKnow = () => {
  const { t } = useTranslation();
  const [suggestions, setSuggestions] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/network/suggestions`, { credentials: "include" });
        if (!r.ok) {
          if (!cancelled) setLoading(false);
          return;
        }
        const d = await r.json();
        if (!cancelled) {
          setSuggestions(d.suggestions || []);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dismiss = async (uid) => {
    // Optimistic remove
    const previous = suggestions;
    setSuggestions((s) => s.filter((p) => p.user_id !== uid));
    try {
      const r = await fetch(`${API}/network/suggestions/dismiss`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: uid }),
      });
      if (!r.ok) throw new Error("dismiss failed");
    } catch {
      setSuggestions(previous);
      toast.error("Couldn't dismiss. Try again.");
    }
  };

  if (loading || suggestions.length === 0) return null;

  const visible = expanded ? suggestions : suggestions.slice(0, 5);
  const hasMore = suggestions.length > 5;

  return (
    <div
      className="rounded-xl p-4"
      style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}
      data-testid="people-you-might-know"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: "var(--color-text-primary)" }}>
          <UserPlus className="w-4 h-4" style={{ color: "var(--color-brand-primary)" }} />
          {t("dashboard.peopleYouMightKnow")}
        </h3>
      </div>

      <ul className="space-y-2">
        {visible.map((p) => {
          const previewNames = (p.mutual_preview || []).map((m) => (m.name || "").split(" ")[0]).filter(Boolean);
          const previewLabel = previewNames.length > 0
            ? t("dashboard.mutualVia", { names: previewNames.slice(0, 2).join(", ") })
            : null;
          return (
            <li
              key={p.user_id}
              className="flex items-center gap-3 rounded-lg p-2"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
              data-testid={`pymk-row-${p.user_id}`}
            >
              <LinkedAvatar username={p.username} className="block flex-shrink-0">
                <Avatar
                  className="h-9 w-9 ring-1 cursor-pointer"
                  style={{ ["--tw-ring-color"]: "rgba(167,139,250,0.30)" }}
                >
                  {p.picture && <AvatarImage src={p.picture} alt={p.name} />}
                  <AvatarFallback
                    className="text-xs font-semibold"
                    style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}
                  >
                    {(p.name || "?").charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </LinkedAvatar>

              <div className="flex-1 min-w-0">
                <LinkedName username={p.username}>
                  <div
                    className="text-xs font-semibold truncate"
                    style={{ color: "var(--color-text-primary)" }}
                    data-testid={`pymk-name-${p.user_id}`}
                  >
                    {p.name || "—"}
                  </div>
                </LinkedName>
                <div
                  className="text-[10px] truncate"
                  style={{ color: "var(--color-text-secondary)" }}
                  data-testid={`pymk-mutual-${p.user_id}`}
                >
                  {t("dashboard.mutualConnections", { count: p.mutual_count })}
                </div>
                {previewLabel && (
                  <div
                    className="text-[10px] truncate"
                    style={{ color: "var(--color-text-placeholder)" }}
                    data-testid={`pymk-via-${p.user_id}`}
                  >
                    {previewLabel}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5 flex-shrink-0">
                {p.username && (
                  <Link
                    to={`/${p.username}`}
                    className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-all hover:scale-[1.03]"
                    style={{
                      background: "var(--color-brand-primary)",
                      color: "var(--color-text-on-brand)",
                      boxShadow: "0 1px 5px rgba(124, 58, 237, 0.30)",
                    }}
                    data-testid={`pymk-invite-${p.user_id}`}
                  >
                    <Plus className="w-3 h-3" />
                    {t("dashboard.addToCircle")}
                  </Link>
                )}
                <button
                  onClick={() => dismiss(p.user_id)}
                  aria-label={t("dashboard.dismissSuggestion")}
                  title={t("dashboard.dismissSuggestion")}
                  className="rounded-full p-1.5 transition-colors hover:bg-white/5"
                  style={{ color: "var(--color-text-placeholder)" }}
                  data-testid={`pymk-dismiss-${p.user_id}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {hasMore && (
        <button
          onClick={() => setExpanded((e) => !e)}
          className="mt-3 w-full text-center text-xs font-medium inline-flex items-center justify-center gap-1 hover:opacity-80"
          style={{ color: "var(--color-text-interactive)" }}
          data-testid="pymk-toggle"
        >
          {expanded ? (
            <>{t("dashboard.showLess")} <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>{t("dashboard.showMore")} <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      )}
    </div>
  );
};

// ── Main Dashboard ────────────────────────────────────────
const Dashboard = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [milestones, setMilestones] = useState(null);
  const [insightsHub, setInsightsHub] = useState(null);
  const [contributors, setContributors] = useState([]);
  const [progress, setProgress] = useState(null);
  const [network, setNetwork] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [showAllInsights, setShowAllInsights] = useState(false);
  const [wheel, setWheel] = useState(null);
  const [gratitude, setGratitude] = useState({ count: 0, pending: 0, pendingAsks: [], cards: [], joinedAt: null });
  const [wheelAreaMap, setWheelAreaMap] = useState({});
  const [gratitudeRequests, setGratitudeRequests] = useState([]);
  const [thankBackPrompts, setThankBackPrompts] = useState([]);
  const [thankBackOpen, setThankBackOpen] = useState(false);
  const [thankBackTarget, setThankBackTarget] = useState(null);
  const [usernameInfo, setUsernameInfo] = useState(null); // {username, username_set_explicitly}
  const [usernameBannerDismissed, setUsernameBannerDismissed] = useState(
    () => localStorage.getItem("username_banner_dismissed") === "true"
  );
  const [dailyNudge, setDailyNudge] = useState(null);
  const [reciprocity, setReciprocity] = useState([]);
  const [activityCollapsed, setActivityCollapsed] = useState(
    () => localStorage.getItem("dashboard_activity_collapsed") !== "false"
  );
  const [insightsTab, setInsightsTab] = useState("insights"); // "insights" | "activity"
  const onboardedFiredRef = useRef(false);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [milRes, hubRes, contRes, progRes, netRes, actRes, wheelRes, gratRes, profRes, gratReqRes, thxRes, nudgeRes, recipRes] = await Promise.all([
        fetch(`${API}/dashboard/milestones`, { credentials: "include" }),
        fetch(`${API}/dashboard/insights-hub`, { credentials: "include" }),
        fetch(`${API}/dashboard/top-contributors`, { credentials: "include" }),
        fetch(`${API}/dashboard/progress`, { credentials: "include" }),
        fetch(`${API}/network`, { credentials: "include" }),
        fetch(`${API}/dashboard/activity`, { credentials: "include" }),
        fetch(`${API}/tools/wheel`, { credentials: "include" }),
        fetch(`${API}/gratitude/wall`, { credentials: "include" }),
        fetch(`${API}/profile`, { credentials: "include" }),
        fetch(`${API}/gratitude/pending-requests`, { credentials: "include" }),
        fetch(`${API}/gratitude/thank-back-prompts`, { credentials: "include" }),
        fetch(`${API}/dashboard/daily-nudge`, { credentials: "include" }),
        fetch(`${API}/dashboard/reciprocity`, { credentials: "include" }),
      ]);
      if (milRes.ok) setMilestones(await milRes.json());
      if (hubRes.ok) setInsightsHub(await hubRes.json());
      if (contRes.ok) { const d = await contRes.json(); setContributors(d.contributors || []); }
      if (progRes.ok) setProgress(await progRes.json());
      if (netRes.ok) setNetwork(await netRes.json());
      if (actRes.ok) setActivity(await actRes.json());
      if (wheelRes.ok) {
        const d = await wheelRes.json();
        setWheel(d.wheel);
        const active = (d.wheel?.areas && d.wheel.areas.length > 0) ? d.wheel.areas : (d.default_areas || []);
        const map = {};
        active.forEach(a => { map[a.key] = a; });
        setWheelAreaMap(map);
      }
      if (gratRes.ok) { const d = await gratRes.json(); setGratitude({ count: (d.cards || []).length, pending: (d.pending_asks || []).length, pendingAsks: d.pending_asks || [], cards: d.cards || [], joinedAt: d.joined_at || null }); }
      if (profRes.ok) { const d = await profRes.json(); setUsernameInfo({ username: d.username, username_set_explicitly: d.username_set_explicitly }); }
      if (gratReqRes.ok) { const d = await gratReqRes.json(); setGratitudeRequests(d.requests || []); }
      if (thxRes.ok) { const d = await thxRes.json(); setThankBackPrompts(d.prompts || []); }
      if (nudgeRes.ok) { const d = await nudgeRes.json(); setDailyNudge(d.nudge || null); }
      if (recipRes.ok) { const d = await recipRes.json(); setReciprocity(d.items || []); }
    } catch {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // FB: fire OnboardedUser when all milestones complete (once per session)
  useEffect(() => {
    if (milestones?.all_complete && !onboardedFiredRef.current) {
      onboardedFiredRef.current = true;
      fbOnboardedUser();
    }
  }, [milestones]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
          style={{ borderColor: "var(--color-brand-primary)", borderTopColor: "transparent" }} />
      </div>
    );
  }

  const showMilestones = milestones && !milestones.all_complete;
  const acceptedNetwork = network.filter((c) => c.status === "accepted");
  const insightItems = insightsHub?.items || [];
  const pendingForMe = insightItems.filter((i) => i.direction === "incoming" && i.status === "pending");
  const dynamicSubtitle = getDynamicSubtitle(insightItems, contributors, acceptedNetwork, milestones);

  const swotProgress = progress?.myself?.tools?.swot?.progress || 0;
  const valuesProgress = progress?.myself?.tools?.core_values?.progress || 0;

  return (
    <div data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* ── Welcome Header — editorial tone matching public profile ── */}
        <div className="mb-5 flex items-center gap-3">
          <div
            className="rounded-full p-[2px] flex-shrink-0"
            style={{
              background:
                "conic-gradient(from 210deg, #7c3aed, #d946ef, #f59e0b, #7c3aed)",
            }}
          >
            <Avatar className="h-10 w-10" style={{ border: "2px solid #fff" }}>
              <AvatarImage src={user?.picture} alt={user?.name} />
              <AvatarFallback className="text-base font-semibold"
                style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}>
                {user?.name?.charAt(0) || "?"}
              </AvatarFallback>
            </Avatar>
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold break-words leading-tight"
                style={{ color: "var(--color-text-primary)" }}>
              {t("dashboard.welcomeBack", { name: user?.name?.split(" ")[0] || "" })}
            </h1>
            {usernameInfo?.username && (
              <Link
                to={`/${usernameInfo.username}`}
                className="text-xs hover:underline"
                style={{ color: "var(--color-text-secondary)" }}
                data-testid="welcome-handle"
              >
                @{usernameInfo.username}
              </Link>
            )}
          </div>
        </div>

        {/* ── Username Banner (auto-assigned, not yet confirmed) ── */}
        {!usernameBannerDismissed && (
          <UsernameBanner
            usernameInfo={usernameInfo}
            navigate={navigate}
            onDismiss={() => {
              localStorage.setItem("username_banner_dismissed", "true");
              setUsernameBannerDismissed(true);
            }}
          />
        )}

        {/* ── Action Banner (pending insights for user) ── */}
        <ActionBanner pendingForMe={pendingForMe} navigate={navigate} />

        {/* ── Gratitude Connection Requests (from public wall) ── */}
        <GratitudeRequestBanner requests={gratitudeRequests} onRefresh={fetchDashboardData} />

        {/* ── Thank-Back Prompts (reciprocity loop) ── */}
        <ThankBackPromptBanner
          prompts={thankBackPrompts}
          onOpen={(p) => { setThankBackTarget(p); setThankBackOpen(true); }}
        />

        {/* ── Gratitude Ask Banner (pending gratitude asks) ── */}
        <GratitudeAskBanner pendingAsks={gratitude.pendingAsks} navigate={navigate} />

        {/* ── Next Step (compact milestones) ── */}
        {showMilestones && (
          <NextStepCard
            milestones={milestones.milestones}
            navigate={navigate}
            onRequestInsight={() => setRequestDialogOpen(true)}
          />
        )}

        {/* Gratitude Pulse moved to right sidebar (above the rhythm) */}

        {/* ── YOUR PEOPLE row — reciprocity nudge only (Mutual Gratitude
            intentionally removed from dashboard to keep focus on action
            items; still available on network/profile pages). ── */}
        {reciprocity.length > 0 && (
          <div className="mb-6" data-testid="your-people-row">
            <div
              className="rounded-xl p-4"
              style={{
                background: "var(--color-surface)",
                border: "0.5px solid var(--color-border-default)",
              }}
              data-testid="reciprocity-card"
            >
                <div className="flex items-center justify-between mb-3">
                  <h3
                    className="text-sm font-semibold flex items-center gap-2"
                    style={{ color: "var(--color-text-primary)" }}
                  >
                    <HeartHandshake
                      className="w-4 h-4"
                      style={{ color: "#E3638E" }}
                    />
                    Waiting for your thank-back
                  </h3>
                  <span
                    className="text-[11px] rounded-full px-2 py-0.5"
                    style={{
                      background: "var(--color-weaknesses-bg)",
                      color: "var(--color-weaknesses-text)",
                    }}
                  >
                    {reciprocity.length}
                  </span>
                </div>
                <ul className="space-y-2.5">
                  {reciprocity.slice(0, 4).map((r) => (
                    <li
                      key={r.user_id}
                      className="flex items-center gap-2.5"
                      data-testid={`reciprocity-item-${r.user_id}`}
                    >
                      <LinkedAvatar username={r.username} className="flex-shrink-0">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={r.picture} alt={r.first_name} />
                          <AvatarFallback
                            className="text-xs"
                            style={{
                              background: "var(--color-brand-ghost)",
                              color: "var(--color-brand-primary)",
                            }}
                          >
                            {r.first_name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                      </LinkedAvatar>
                      <div className="flex-1 min-w-0 leading-tight">
                        <p
                          className="text-sm font-medium truncate"
                          style={{ color: "var(--color-text-primary)" }}
                        >
                          <LinkedName username={r.username} name={r.first_name}>
                            {r.first_name}
                          </LinkedName>
                        </p>
                        {r.username && (
                          <p
                            className="text-[10px] truncate"
                            style={{ color: "var(--color-text-placeholder)" }}
                          >
                            @{r.username}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          setThankBackTarget({
                            target_name: r.first_name,
                            target_picture: r.picture,
                            target_username: r.username,
                            connection_id: r.user_id,
                            card_id: r.card_id,
                          });
                          setThankBackOpen(true);
                        }}
                        className="text-xs font-semibold rounded-full px-3 py-1 transition-colors"
                        style={{
                          background: "var(--color-brand-ghost)",
                          color: "var(--color-brand-primary)",
                        }}
                        data-testid={`thank-back-btn-${r.user_id}`}
                      >
                        Thank back
                      </button>
                    </li>
                  ))}
                  {reciprocity.length > 4 && (
                    <li
                      className="text-[11px] italic pt-1"
                      style={{ color: "var(--color-text-secondary)" }}
                    >
                      + {reciprocity.length - 4} more waiting
                    </li>
                  )}
                </ul>
              </div>
          </div>
        )}

        {/* ── Main Grid ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left Column (2/3) ── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Insights + Recent Activity — unified tabbed box */}
            <div className="rounded-xl p-5" style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }} data-testid="insights-hub">
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="inline-flex rounded-full p-0.5" style={{ background: "var(--color-surface-subtle)" }}>
                  <button
                    onClick={() => setInsightsTab("insights")}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                    style={{
                      background: insightsTab === "insights" ? "var(--color-brand-primary)" : "transparent",
                      color: insightsTab === "insights" ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                    }}
                    data-testid="insights-tab-insights"
                  >
                    <MessageSquare className="w-3.5 h-3.5" />
                    {t("dashboard.tabInsights")}
                    {insightsHub?.pending_count > 0 && (
                      <span className="text-[10px] rounded-full px-1.5 py-0.5 ml-0.5"
                        style={{
                          background: insightsTab === "insights" ? "rgba(255,255,255,0.25)" : "var(--color-brand-primary)",
                          color: insightsTab === "insights" ? "var(--color-text-on-brand)" : "var(--color-text-on-brand)",
                        }}>
                        {insightsHub.pending_count}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setInsightsTab("activity")}
                    className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
                    style={{
                      background: insightsTab === "activity" ? "var(--color-brand-primary)" : "transparent",
                      color: insightsTab === "activity" ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                    }}
                    data-testid="insights-tab-activity"
                  >
                    <Clock className="w-3.5 h-3.5" />
                    {t("dashboard.tabActivity")}
                    {activity.length > 0 && (
                      <span className="text-[10px] rounded-full px-1.5 py-0.5 ml-0.5"
                        style={{
                          background: insightsTab === "activity" ? "rgba(255,255,255,0.25)" : "var(--color-surface)",
                          color: insightsTab === "activity" ? "var(--color-text-on-brand)" : "var(--color-text-secondary)",
                        }}>
                        {activity.length}
                      </span>
                    )}
                  </button>
                </div>
              </div>

              {insightsTab === "insights" && (insightItems.length > 0 ? (
                <div className="space-y-1">
                  {(showAllInsights ? insightItems : insightItems.slice(0, 5)).map((item) => (
                    <InsightItem key={item.request_id} item={item} navigate={navigate} />
                  ))}
                  {insightItems.length > 5 && (
                    <button
                      onClick={() => setShowAllInsights(!showAllInsights)}
                      className="w-full text-center text-xs font-medium py-2 mt-1 rounded-lg transition-colors"
                      style={{ color: "var(--color-text-interactive)" }}
                      data-testid="toggle-all-insights"
                    >
                      {showAllInsights ? t("common.cancel") : t("dashboard.seeAllInsights", { count: insightItems.length })}
                    </button>
                  )}
                </div>
              ) : (
                /* Personalized empty state */
                <div className="text-center py-6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                    style={{ background: "var(--color-brand-ghost)" }}>
                    <MessageSquare className="w-5 h-5" style={{ color: "var(--color-brand-light)" }} />
                  </div>
                  {acceptedNetwork.length > 0 ? (
                    <>
                      <p className="text-sm mb-1" style={{ color: "var(--color-text-secondary)" }}>
                        No insights yet
                      </p>
                      <p className="text-xs mb-3" style={{ color: "var(--color-text-placeholder)" }}>
                        {acceptedNetwork.slice(0, 2).map(c => c.display_name || c.name).join(", ")}
                        {acceptedNetwork.length > 2 ? ` and ${acceptedNetwork.length - 2} others` : ""}
                        {" "}are in your circle. Ask them for their perspective.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm mb-1" style={{ color: "var(--color-text-secondary)" }}>No insights yet</p>
                      <p className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>
                        Build your circle first, then ask for perspectives.
                      </p>
                    </>
                  )}
                </div>
              ))}

              {insightsTab === "activity" && (
                activity.length > 0 ? (
                  <div className="space-y-3">
                    {activity.slice(0, 8).map((item, idx) => {
                      const isInsight = item.type === "insight_received";
                      const isGratitude = item.type === "gratitude_received";
                      const isConnRequest = item.type === "connection_request";
                      const isConnAccepted = item.type === "connection_accepted";
                      const isConnDeclined = item.type === "connection_declined";
                      const isConnection = isConnRequest || isConnAccepted || isConnDeclined || item.type === "connection";
                      const bg = isInsight ? "var(--color-brand-ghost)"
                        : isGratitude ? "var(--color-weaknesses-bg)"
                        : isConnection ? "var(--color-strengths-bg)"
                        : "var(--color-surface-subtle)";
                      const Icon = isInsight ? Lightbulb
                        : isGratitude ? HeartHandshake
                        : isConnection ? Users
                        : Bell;
                      const iconColor = isInsight ? "var(--color-brand-primary)"
                        : isGratitude ? "var(--color-weaknesses-text)"
                        : isConnection ? "var(--color-strengths-text)"
                        : "var(--color-text-secondary)";
                      return (
                        <div key={(item.title || "") + idx} className="flex items-start gap-2.5">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: bg }}>
                            <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
                          </div>
                          {item.from_user_picture && (
                            <LinkedAvatar username={item.from_user_username} className="flex-shrink-0 mt-0.5 flex">
                              <Avatar className="h-8 w-8">
                                <AvatarImage src={item.from_user_picture} alt={item.from_user_name || ""} />
                                <AvatarFallback className="text-xs" style={{ background: "var(--color-brand-ghost)", color: "var(--color-brand-primary)" }}>
                                  {(item.from_user_name || "?").charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            </LinkedAvatar>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>{item.title}</p>
                            <p className="text-xs" style={{ color: "var(--color-text-placeholder)" }}>{item.message}</p>
                          </div>
                          {item.read === false && (
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
                              style={{ color: "var(--color-brand-primary)", background: "var(--color-brand-ghost)" }}>
                              New
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                      style={{ background: "var(--color-brand-ghost)" }}>
                      <Clock className="w-5 h-5" style={{ color: "var(--color-brand-light)" }} />
                    </div>
                    <p className="text-sm" style={{ color: "var(--color-text-secondary)" }}>
                      Nothing recent yet
                    </p>
                    <p className="text-xs mt-0.5" style={{ color: "var(--color-text-placeholder)" }}>
                      Your activity stream will appear here as you use the app.
                    </p>
                  </div>
                )
              )}
            </div>

            {/* Your reflections — compact chip strip (demoted from gauge cards).
                Each chip = one tool + one dynamic insight subtitle, no gauges. */}
            <div data-testid="your-reflections">
              <h3 className="text-base font-semibold mb-3" style={{ color: "var(--color-text-primary)" }}>
                {t("dashboard.reflections")}
              </h3>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                {[
                  {
                    to: "/tools/swot",
                    icon: Target,
                    label: t("tools.swot"),
                    subtitle: swotProgress > 0 ? t("dashboard.swotComplete", { pct: swotProgress }) : "—",
                    testId: "chip-swot",
                    color: "var(--color-strengths-text)",
                    trackName: "SWOT",
                  },
                  {
                    to: "/tools/values",
                    icon: Heart,
                    label: t("tools.values"),
                    subtitle: valuesProgress > 0 ? t("dashboard.valuesSet", { count: 7 }) : "—",
                    testId: "chip-values",
                    color: "var(--color-brand-primary)",
                    trackName: "Core Values",
                  },
                  {
                    to: "/tools/wheel",
                    icon: CircleDot,
                    label: t("tools.wheel"),
                    subtitle: wheel && (wheel.areas || []).length > 0
                      ? `${(wheel.areas || []).filter(a => a.score).length}/${(wheel.areas || []).length}`
                      : t("dashboard.wheelNotRated"),
                    testId: "chip-wheel",
                    color: "#14b8a6",
                    trackName: "Wheel of Life",
                  },
                  {
                    to: "/tools/gratitude",
                    icon: HeartHandshake,
                    label: t("tools.gratitude"),
                    subtitle: gratitude.count > 0
                      ? t("dashboard.notesCount", { count: gratitude.count })
                      : "—",
                    testId: "chip-gratitude",
                    color: "var(--color-brand-primary)",
                    trackName: "Gratitude Wall",
                  },
                ].map(item => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="group flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-all hover:shadow-sm"
                      style={{ background: "var(--color-surface)", border: "0.5px solid var(--color-border-default)" }}
                      data-testid={item.testId}
                      onClick={() => trackToolOpened(item.trackName, "dashboard")}
                    >
                      <Icon className="w-4 h-4 flex-shrink-0" style={{ color: item.color }} />
                      <div className="flex-1 min-w-0 leading-tight">
                        <div className="text-xs font-semibold truncate" style={{ color: "var(--color-text-primary)" }}>
                          {item.label}
                        </div>
                        <div className="text-[10px] truncate" style={{ color: "var(--color-text-secondary)" }}>
                          {item.subtitle}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* ── Right Column (1/3) ── */}
          <div className="space-y-6">

            {/* SWOT Video Nudge — show when user hasn't started SWOT */}
            {swotProgress === 0 && <SwotVideoNudge />}

            {/* Circle Preview */}
            <CirclePreview connections={network} />

            {/* People you might know — mutual-connection suggestions, hides
                when the backend returns no candidates */}
            <PeopleYouMightKnow />

            {/* Gratitude Pulse — compact summary (above the rhythm) */}
            <GratitudePulse cards={gratitude.cards} wheelAreaMap={wheelAreaMap} />

            {/* Gratitude rhythm — compact timeline sparkline */}
            {gratitude.cards.length > 0 && (
              <div data-testid="dashboard-rhythm-sidebar">
                <GratitudeTimeline
                  cards={gratitude.cards}
                  wheelAreaMap={wheelAreaMap}
                  variant="dashboard"
                  joinedAt={gratitude.joinedAt}
                  joinedPicture={user?.picture}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Request Insight Dialog */}
      <InsightRequestDialog
        open={requestDialogOpen}
        onOpenChange={setRequestDialogOpen}
        network={network}
        toolType="swot"
        toolLabel="Personal Development"
        onRequestSent={fetchDashboardData}
        showToolPicker
      />

      {/* Thank-Back mini composer */}
      {thankBackTarget && (
        <ThankBackSheet
          open={thankBackOpen}
          onOpenChange={setThankBackOpen}
          targetName={thankBackTarget.target_name}
          targetPicture={thankBackTarget.target_picture}
          targetUsername={thankBackTarget.target_username}
          connectionId={thankBackTarget.connection_id}
          originalCardId={thankBackTarget.card_id}
          onSuccess={fetchDashboardData}
        />
      )}
    </div>
  );
};

export default Dashboard;
